/**
 * `/connexion/$code` (publique, migration 0334) : ouverte en scannant le QR
 * code d'un profil. Sans session → connexion / inscription sur place (retour
 * ici après l'onboarding). Connecté → aperçu (prénom, avatar, type de lien)
 * puis « Me relier » : le lien est créé tout de suite.
 */

import { useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CompassLogo,
} from '@jeraisonne/ui';
import { AvatarCircle } from '@/components/avatar-circle';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { setReturnTo } from '@/lib/return-to';
import {
  connectErrorMessage,
  connectRelationLabel,
  contactsPathFor,
  normalizeConnectCode,
  useConnectProfile,
  useConnectWithCode,
} from '@/lib/connect-code';

export function ConnectPage({ code: rawCode }: { code: string }) {
  const code = normalizeConnectCode(rawCode);
  const { session, profile, loading } = useAuth();
  const preview = useConnectProfile(code, !!session && !!profile);
  const connect = useConnectWithCode();

  // Un nouveau compte passe par l'onboarding : on revient ici ensuite.
  useEffect(() => {
    if (code && !session) setReturnTo(`/connexion/${code}`);
  }, [code, session]);

  const p = preview.data;
  return (
    <div className="min-h-dvh bg-transparent">
      <header className="flex justify-center px-6 py-8">
        <CompassLogo size="lg" wordmarkClassName="text-ink-900" />
      </header>
      <main className="mx-auto w-full max-w-md px-4 pb-16">
        {!code ? (
          <Card>
            <CardHeader>
              <CardTitle>Code invalide</CardTitle>
              <CardDescription>Ce lien de connexion n’est pas valide.</CardDescription>
            </CardHeader>
          </Card>
        ) : loading ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ) : !session ? (
          <OtpAuthForm
            initialMode="signup"
            renderHeader={({ step, email }) =>
              step === 'otp' ? (
                <>
                  <CardTitle>Vérifie ta boîte mail</CardTitle>
                  <CardDescription>
                    Entre le code à 6 chiffres envoyé à <strong>{email}</strong>.
                  </CardDescription>
                </>
              ) : (
                <>
                  <CardTitle>Se relier sur JeRaisonne</CardTitle>
                  <CardDescription>
                    Connecte-toi (ou crée ton compte) pour te relier à la personne qui t’a montré
                    son QR code.
                  </CardDescription>
                </>
              )
            }
          />
        ) : !profile ? (
          <Card>
            <CardHeader>
              <CardTitle>Termine ton inscription</CardTitle>
              <CardDescription>Encore une étape avant de te relier.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button fullWidth onClick={() => (window.location.href = '/onboarding')}>
                Continuer
              </Button>
            </CardContent>
          </Card>
        ) : connect.isSuccess ? (
          <Card>
            <CardContent className="space-y-4 pt-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" aria-hidden />
              <p className="text-ink-900 font-semibold">
                C’est fait : tu es relié{p?.first_name ? ` à ${p.first_name}` : ''} !
              </p>
              <a
                href={contactsPathFor(profile.role)}
                className="bg-primary-500 inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-white active:scale-[0.98]"
              >
                Voir mes contacts
              </a>
            </CardContent>
          </Card>
        ) : preview.isLoading ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ) : !p || p.status !== 'ok' ? (
          <Card>
            <CardHeader>
              <CardTitle>Impossible de se relier</CardTitle>
              <CardDescription>{connectErrorMessage(p?.status ?? 'unknown')}</CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <Card>
            <CardContent className="space-y-4 pt-6 text-center">
              <div className="flex justify-center">
                <AvatarCircle name={p.first_name ?? '?'} src={p.avatar_url ?? null} size="lg" />
              </div>
              <p className="text-ink-900 text-lg font-bold">{p.first_name ?? 'Ce compte'}</p>
              {p.relation ? (
                <>
                  <p className="text-ink-600 text-sm">
                    {connectRelationLabel(p.relation, p.role ?? '')}
                  </p>
                  <Button
                    fullWidth
                    size="lg"
                    loading={connect.isPending}
                    onClick={() => code && connect.mutate(code)}
                  >
                    Me relier à {p.first_name ?? 'ce compte'}
                  </Button>
                  {connect.error && (
                    <p role="alert" className="text-error-600 text-sm">
                      {connectErrorMessage(connect.error.message)}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-ink-600 text-sm">{connectErrorMessage('unsupported')}</p>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
